/**
 * The five engagement lines a volunteer can check with the person at the
 * door, per `docs/API.md` §8: each one becomes its own `POST /engagements/`
 * with `consent: true` when the household sheet is saved.
 */
import { t } from '../../../i18n/fr';
import { ENGAGEMENT_HINT_KEYS, ENGAGEMENT_LABEL_KEYS } from '../labels';

import type { EngagementKind } from '../../../api/types';

/** One row's local draft: whether it is checked, plus contact fields for `contact`. */
export interface EngagementDraft {
  checked: boolean;
  email: string;
  phone: string;
}

export type EngagementDrafts = Record<EngagementKind, EngagementDraft>;

/** The five kinds, in the order the household sheet shows them. */
export const ENGAGEMENT_KINDS: EngagementKind[] = [
  'contact',
  'newsletter',
  'support_campaign',
  'building_referent',
  'join_group',
];

/**
 * Build the empty draft every household sheet starts from.
 *
 * @returns One unchecked, empty draft per engagement kind.
 */
export function emptyEngagementDrafts(): EngagementDrafts {
  return {
    contact: { checked: false, email: '', phone: '' },
    newsletter: { checked: false, email: '', phone: '' },
    support_campaign: { checked: false, email: '', phone: '' },
    building_referent: { checked: false, email: '', phone: '' },
    join_group: { checked: false, email: '', phone: '' },
  };
}

export interface EngagementListProps {
  value: EngagementDrafts;
  onChange: (value: EngagementDrafts) => void;
  disabled?: boolean;
}

/**
 * Render the five engagement rows, each a checkbox plus, for "Prise de
 * contact" only, the e-mail and phone fields to reach the person on.
 *
 * @param props - Current drafts, a change handler and an optional disabled state.
 * @returns The engagement list element.
 */
export function EngagementList({ value, onChange, disabled = false }: EngagementListProps) {
  function toggle(kind: EngagementKind) {
    onChange({ ...value, [kind]: { ...value[kind], checked: !value[kind].checked } });
  }

  function updateField(kind: EngagementKind, field: 'email' | 'phone', fieldValue: string) {
    onChange({ ...value, [kind]: { ...value[kind], [field]: fieldValue } });
  }

  return (
    <div>
      {ENGAGEMENT_KINDS.map((kind) => {
        const draft = value[kind];
        const inputId = `engagement-${kind}`;
        const hintKey = ENGAGEMENT_HINT_KEYS[kind];
        return (
          <div className="eng" key={kind}>
            <input
              id={inputId}
              type="checkbox"
              checked={draft.checked}
              onChange={() => toggle(kind)}
              disabled={disabled}
            />
            <div>
              <label className="lib" htmlFor={inputId}>
                {t(ENGAGEMENT_LABEL_KEYS[kind])}
              </label>
              {kind === 'contact' ? (
                <>
                  <input
                    className="ui-input"
                    style={{ marginTop: 'var(--space-1)' }}
                    type="email"
                    placeholder={t('household.engagements.contact.emailPlaceholder')}
                    aria-label={t('household.engagements.contact.emailPlaceholder')}
                    value={draft.email}
                    onChange={(event) => updateField('contact', 'email', event.target.value)}
                    disabled={disabled}
                  />
                  <input
                    className="ui-input"
                    style={{ marginTop: 'var(--space-1)' }}
                    type="tel"
                    placeholder={t('household.engagements.contact.phonePlaceholder')}
                    aria-label={t('household.engagements.contact.phonePlaceholder')}
                    value={draft.phone}
                    onChange={(event) => updateField('contact', 'phone', event.target.value)}
                    disabled={disabled}
                  />
                </>
              ) : (
                <span className="muted">{hintKey ? t(hintKey) : null}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
